import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../store/rootReducer';
import {setOpenBundleId} from '../../store/settingsSlice';
import {useUrlPanelState} from '../../hooks/useUrlState';
import StorefrontBundleModal from './StorefrontBundleModal';

/**
 * App-level host for the bundle editor.
 *
 * Bundles have no route — they're only ever edited in a modal that some screen happens to render.
 * That leaves anything outside the React tree with no way to open one, which is why a bulk "added
 * to bundle" toast previously had no follow-up link. Dispatching setOpenBundleId now opens the
 * editor wherever the user currently is.
 *
 * Two sources can open it, and they answer different needs:
 *  - Redux (setOpenBundleId) is the in-session intent — the toast opens the bundle over whatever
 *    page you were on, deliberately without navigating you away from it.
 *  - The URL (?panel=bundle&instance=<id>) is the addressable one, so a link can open a bundle on
 *    any page. Since the modal is hosted above the routes, that param works from anywhere.
 *
 * Redux wins when both are set: it's the more recent action of the two.
 *
 * Mounted once beside the global ToastContainer. The modal is rendered only when an id is set, so
 * this costs nothing (and issues no queries) the rest of the time.
 */
const BundleModalHost: React.FC = () => {
    const dispatch = useDispatch();
    const reduxBundleId = useSelector((state: RootState) => state.settings.openBundleId);
    const bundlePanel = useUrlPanelState('bundle');
    const bundleId = reduxBundleId ?? (bundlePanel.isOpen ? bundlePanel.instanceId : undefined);

    if (!bundleId) return null;

    return (
        <StorefrontBundleModal
            bundleId={bundleId}
            isOpen={true}
            onDismiss={() => {
                // Clear both, or dismissing a URL-opened bundle would leave the param to reopen it.
                dispatch(setOpenBundleId(undefined));
                bundlePanel.close();
            }}
        />
    );
};

export default BundleModalHost;
